/**
 * CF7 multistep “falso” seguro con envío AJAX
 * - Navegación por pasos con botones type="button"
 * - Selectores acotados al form
 * - Progreso generado dentro del form
 * - Pantalla de “gracias” con eventos nativos de CF7
 * - Habilitado del submit cuando inputs/checkbox cumplen
 */

class Cf7MultiStep {
  constructor(form) {
    this.form = form;
    this.steps = Array.from(this.form.querySelectorAll('.step'));
    this.current = 0;

    // Botones de pasos
    this.nextButtons = Array.from(this.form.querySelectorAll('.cf7-next'));
    this.prevButtons = Array.from(this.form.querySelectorAll('.cf7-prev'));

    this.bindListeners();
    this.initProgressBlocks();
    this.showStep(this.current);

    // Si el template trae un “gracias” visible por defecto, lo ocultamos hasta el envío real
    const ty = this.form.querySelector('.thank-you');
    if (ty) ty.style.display = 'none';
  }

  bindListeners() {
    // Navegación de pasos (evitar submit accidental)
    this.nextButtons.forEach(btn => {
      btn.setAttribute('type', 'button');
      btn.addEventListener('click', (e) => { e.preventDefault(); this.handleNext(); });
    });
    this.prevButtons.forEach(btn => {
      btn.setAttribute('type', 'button');
      btn.addEventListener('click', (e) => { e.preventDefault(); this.handlePrev(); });
    });

    // Eventos nativos de CF7 (estado real del envío)
    this.form.addEventListener('wpcf7mailsent', () => this.onSent());
    this.form.addEventListener('wpcf7invalid', () => this.onInvalid());
    this.form.addEventListener('wpcf7mailfailed', () => this.onFailed());
    this.form.addEventListener('wpcf7spam', () => this.onFailed());

    // Habilitar/deshabilitar submit según cumplimentación
    this.setupSubmitEnabler();
  }

  initProgressBlocks() {
    // Cada form puede tener su propio indicador
    const indicators = this.form.querySelectorAll('.progress-blocks');
    indicators.forEach(indicator => {
      if (indicator.children.length === 0) {
        for (let i = 0; i < 16; i++) {
          indicator.appendChild(document.createElement('span'));
        }
      }
    });
  }

  handleNext() {
    if (this.validateStep(this.current) && this.current < this.steps.length - 1) {
      this.current++;
      this.showStep(this.current);
      this.updateTitlesVisibility(this.current);
    }
  }

  handlePrev() {
    if (this.current > 0) {
      this.current--;
      this.showStep(this.current);
      this.updateTitlesVisibility(this.current);
    }
  }

  updateTitlesVisibility(idx) {
    const titleStep1 = this.form.querySelector('.contact__title--step1');
    const titleStep2 = this.form.querySelector('.contact__title--step2');
    const titleStep3 = this.form.querySelector('.contact__title--step3');

    // Step 1 vs Step 2
    if (titleStep1 && titleStep2) {
      const first = (idx === 0);
      titleStep1.classList.toggle('show', first);
      titleStep1.classList.toggle('hide', !first);
      titleStep1.setAttribute('aria-hidden', String(!first));
      titleStep2.classList.toggle('show', !first);
      titleStep2.classList.toggle('hide', first);
      titleStep2.setAttribute('aria-hidden', String(first));
    }

    // Step 3 lo controlamos al “sent”
    if (titleStep3) {
      const isThankYou = (idx >= 2);
      titleStep3.classList.toggle('show', isThankYou);
      titleStep3.classList.toggle('hide', !isThankYou);
      titleStep3.setAttribute('aria-hidden', String(!isThankYou));
    }
  }

  showStep(idx) {
    this.steps.forEach((s, i) => {
      s.style.display = (i === idx ? 'block' : 'none');
    });
    this.updateProgressIndicator();
  }

  validateStep(idx) {
    // Validación básica UI: respeta aria-required que CF7 coloca
    const step = this.steps[idx];
    if (!step) return true;

    const inputs = step.querySelectorAll('[aria-required="true"]');
    let ok = true;
    inputs.forEach(i => {
      const val = (i.value ?? '').trim();
      if (!val) {
        i.classList.add('wpcf7-not-valid');
        ok = false;
      } else {
        i.classList.remove('wpcf7-not-valid');
      }
    });
    return ok;
  }

  updateProgressIndicator() {
    const indicator = this.form.querySelector('.contact__navigation');
    if (!indicator) return;

    const blocks = indicator.querySelectorAll('span');
    const totalSteps = Math.max(1, Math.min(3, this.steps.length)); // asumimos 3
    // mapear paso actual (1..totalSteps) a bloques (1..16)
    let progress = Math.round(((this.current + 1) / totalSteps) * 16);
    progress = Math.max(1, Math.min(progress, blocks.length));
    indicator.setAttribute('data-progress', progress.toString());

    blocks.forEach((block, index) => {
      block.classList.toggle('active', index < progress);
    });
  }

  onSent() {
    // Mostrar “gracias”
    this.steps.forEach(s => (s.style.display = 'none'));
    const tydiv = this.form.querySelector('.thank-you');
    if (tydiv) tydiv.style.display = 'block';

    // Títulos
    const titleStep1 = this.form.querySelector('.contact__title--step1');
    const titleStep3 = this.form.querySelector('.contact__title--step3');
    if (titleStep1) { titleStep1.classList.add('hide'); titleStep1.classList.remove('show'); }
    if (titleStep3) { titleStep3.classList.add('show'); titleStep3.classList.remove('hide'); }

    // Progreso a tope
    const indicator = this.form.querySelector('.contact__navigation');
    if (indicator) indicator.setAttribute('data-progress', '16');
    const blocks = this.form.querySelectorAll('.contact__navigation span');
    blocks.forEach(b => b.classList.add('active'));

    // dataLayer: empuja el valor de la empresa (no el nodo)
    const companyName = this.form.querySelector('input[name="your-company"]')?.value || '';
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'send_contactForm', item_company: companyName });
  }

  onInvalid() {
    // Mantén el paso actual; deja que CF7 pinte sus errores
    this.updateProgressIndicator();
  }

  onFailed() {
    // Si hay error/spam, no avanzamos; puedes mostrar feedback adicional si quieres
    this.updateProgressIndicator();
  }

  setupSubmitEnabler() {
    const submitButton = this.form.querySelector('input[type="submit"], button[type="submit"]');
    if (!submitButton) return;

    const inputs = this.form.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], textarea[name="your-message"]');
    // Aceptación (selector robusto: wrapper o input con clase)
    const acceptanceInput =
      this.form.querySelector('.wpcf7-acceptance input[type="checkbox"]') ||
      this.form.querySelector('input.accept-box');

    const checkFormFilled = () => {
      const allFilled = Array.from(inputs).every(i => (i.value ?? '').trim());
      const okCheck = acceptanceInput ? !!acceptanceInput.checked : true;
      submitButton.disabled = !(allFilled && okCheck);
    };

    // Estado inicial
    submitButton.disabled = true;
    checkFormFilled();

    inputs.forEach(i => i.addEventListener('input', checkFormFilled));
    if (acceptanceInput) acceptanceInput.addEventListener('change', checkFormFilled);
  }
}

// Inicialización global (soporta múltiples forms CF7 en la página)
function initCf7MultiStep() {
  document.querySelectorAll('.wpcf7-form').forEach(form => {
    // Evitar doble init
    if (form.__cf7MultiInit) return;
    form.__cf7MultiInit = true;
    new Cf7MultiStep(form);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initCf7MultiStep();
});